import {
    AfterViewInit,
    Component,
    ElementRef,
    OnInit,
    ViewChild
} from '@angular/core';

import {
    FormBuilder,
    FormGroup,
    ReactiveFormsModule,
    Validators,
    FormsModule
} from '@angular/forms';

import { CommonModule } from '@angular/common';
import {
    ActivatedRoute,
    Router,
    RouterModule
} from '@angular/router';

import { HttpErrorResponse } from '@angular/common/http';

import { I18NextModule } from 'angular-i18next';
import i18next from 'i18next';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';

import { CoreService } from 'src/app/core/services/core.service';
import { StorageService } from 'src/app/core/storage/storage.service';
import { ControlErrors } from 'src/app/shared/utils/generateError';
import { Collective } from 'src/app/shared/model/user.model';
import { UserRegister } from 'src/app/shared/model/register.model';
import { ErrorWithGuideComponent } from 'src/app/shared/components/error-with-guide/error-with-guide.component';
// CRMSP-7485 - Empleados VO
@Component({
    standalone: true,
    selector: 'app-company-vo-register',
    templateUrl: './company-vo-register.component.html',
    styleUrls: ['./company-vo-register.component.scss'],
    imports: [
        CommonModule,
        I18NextModule,
        ReactiveFormsModule,
        FormsModule,
        RouterModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        MatSelectModule,
        ErrorWithGuideComponent
    ]
})
export class CompanyVoRegisterComponent implements OnInit, AfterViewInit {

    @ViewChild('autofocus')
    autofocus!: ElementRef<HTMLInputElement>;

    group = 'COMPANY_VO';

    codeValidated = false;
    accessRequestSuccess = false;
    requestType: 'ACCESS_REQUEST' | 'USER_CREATION' = 'ACCESS_REQUEST';

    spinner = false;

    errorMessage = '';
    loginErrorMessage: string | null = null;
    showDownloadGuideLink = false;

    userRegister: UserRegister = {
        nis: '',
        password: '',
        repeatPassword: ''
    };

    pattern =
        '^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[ -/:-@[-`{-~]).{12,60}$';

    registerForm: FormGroup = this.fb.group({
        validationCode: ['', Validators.required],
        requestType: ['ACCESS_REQUEST', Validators.required],
        nis: ['', [Validators.required, Validators.pattern(/^(?:\d{8}|[XYZ]\d{7})[A-Z]$/i)]],

        email: [''],
        name: [''],
        surname: [''],
        phone: [''],

        password: ['', [Validators.pattern(this.pattern)]],
        repeatPassword: ['', [Validators.pattern(this.pattern)]]
    });

    constructor(
        private fb: FormBuilder,
        private router: Router,
        private route: ActivatedRoute,
        private coreService: CoreService,
        private storageService: StorageService,
        private controlErrors: ControlErrors
    ) { }

    ngOnInit(): void {
        this.route.queryParams.subscribe((queryParams) => {
            if (queryParams['group']) {
                this.group = queryParams['group'];
            }

            this.updateValidators();
        });
    }

    ngAfterViewInit(): void {
        this.autofocus?.nativeElement?.focus();
    }

    controlInput(input: string): boolean {
        return (
            this.registerForm?.controls[input]?.invalid &&
            this.registerForm?.controls[input]?.touched
        );
    }

    validateCode(): void {
        const validationCodeControl = this.registerForm.get('validationCode');

        if (validationCodeControl?.invalid) {
            validationCodeControl.markAsTouched();
            return;
        }

        this.spinner = true;
        this.errorMessage = '';
        this.loginErrorMessage = null;
        this.showDownloadGuideLink = false;

        const code = validationCodeControl?.value;

        this.coreService
            .validateCompanyCode(code)
            .subscribe({
                next: () => {
                    this.spinner = false;
                    this.codeValidated = true;
                    this.updateValidators();
                },
                error: (/*e: HttpErrorResponse*/) => {
                    this.spinner = false;
                    this.errorMessage = i18next.t('validationCodeErr');
                }
            });
    }

    changeRequestType(
        value: 'ACCESS_REQUEST' | 'USER_CREATION'
    ): void {
        this.requestType = value;
        this.updateValidators();
    }

    updateValidators(): void {
        const email = this.registerForm.get('email');
        const name = this.registerForm.get('name');
        const surname = this.registerForm.get('surname');
        const phone = this.registerForm.get('phone');

        const password = this.registerForm.get('password');
        const repeatPassword = this.registerForm.get('repeatPassword');

        if (this.requestType === 'ACCESS_REQUEST') {
            email?.setValidators([
                Validators.required,
                Validators.email
            ]);

            name?.setValidators([
                Validators.required
            ]);

            surname?.setValidators([
                Validators.required
            ]);

            phone?.setValidators([
                Validators.required,
                Validators.pattern('^[0-9]{9}$')
            ]);

            password?.clearValidators();
            repeatPassword?.clearValidators();

        } else {
            email?.clearValidators();
            name?.clearValidators();
            surname?.clearValidators();
            phone?.clearValidators();

            password?.setValidators([
                Validators.required,
                Validators.pattern(this.pattern)
            ]);

            repeatPassword?.setValidators([
                Validators.required,
                Validators.pattern(this.pattern)
            ]);
        }

        email?.updateValueAndValidity();
        name?.updateValueAndValidity();
        surname?.updateValueAndValidity();
        phone?.updateValueAndValidity();

        password?.updateValueAndValidity();
        repeatPassword?.updateValueAndValidity();
    }

    onSubmit(): void {
        this.errorMessage = '';
        this.loginErrorMessage = null;
        this.showDownloadGuideLink = false;

        if (!this.registerForm.valid) {
            this.registerForm.markAllAsTouched();
            return;
        }

        if (this.requestType === 'ACCESS_REQUEST') {
            this.submitAccessRequest();
            return;
        }

        if (this.requestType === 'USER_CREATION') {
            this.registerUser();
        }
    }

    private submitAccessRequest(): void {
        this.spinner = true;
        this.accessRequestSuccess = false;

        this.coreService
            .registerCompanyDigital(
                this.registerForm.value.validationCode,
                this.registerForm.value.nis,
                this.registerForm.value.email,
                this.registerForm.value.name,
                this.registerForm.value.surname,
                this.registerForm.value.phone
            )
            .subscribe({
                next: () => {
                    this.spinner = false;

                    console.log('Solicitud enviada correctamente');
                    this.accessRequestSuccess = true;
                },
                error: (/*e: HttpErrorResponse*/) => {
                    this.spinner = false;
                    this.accessRequestSuccess = false;
                    this.errorMessage = i18next.t('accessRequestErr');
                }
            });
    }

    private registerUser(): void {
        this.spinner = true;

        if (
            this.registerForm.value.password !==
            this.registerForm.value.repeatPassword
        ) {
            this.spinner = false;
            this.errorMessage = i18next.t('passwordErr');
            return;
        }

        this.userRegister = {
            nis: this.registerForm.value.nis,
            password: this.registerForm.value.password,
            repeatPassword: this.registerForm.value.repeatPassword
        };

        this.storageService.set('userRegister', this.userRegister);

        this.coreService
            .registerUser(
                this.registerForm.value.nis,
                this.registerForm.value.password,
                undefined,
                Collective.EXTERNVO
            )
            .subscribe({
                next: () => {
                    this.spinner = false;

                    this.router.navigate(['register/verification-code'], {
                        queryParams: {
                            group: this.group
                        }
                    });
                },
                error: (e: HttpErrorResponse) => {
                    this.handleRegisterError(e);
                }
            });
    }

    errorMsg(input: string) {
        if (
            this.registerForm?.controls[input]?.invalid &&
            this.registerForm?.controls[input]?.touched
        ) {
            return 'error-message';
        }

        return 'info-text';
    }

    private handleRegisterError(e: HttpErrorResponse): void {
        this.spinner = false;

        if (e.status === 406 && e.error.message === '5003') {
            this.errorMessage = this.controlErrors.generateError(
                e.error.message,
                i18next.t('errorNoPhones')
            );
        } else if (e.status === 406 && e.error.message === '5001') {
            this.errorMessage = this.controlErrors.generateError(
                e.error.message,
                i18next.t('re-verificationError')
            );
        } else if (e.status === 406 && e.error.message === '5005') {
            this.errorMessage = this.controlErrors.generateError(
                e.error.message,
                i18next.t('categoryError')
            );
        } else if (e.status === 412) {
            this.errorMessage = this.controlErrors.generateError(
                e.error.message,
                i18next.t('loginNoPss')
            );
        } else {
            this.errorMessage = this.controlErrors.generateError(
                e.error?.message,
                i18next.t('genericErr')
            );

            this.loginErrorMessage = i18next.t('loginAdditionalMessage');
            this.showDownloadGuideLink = true;
        }
    }

}